import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { fetchDataByConfig, getCitiesList } from '../../ManageDealDrawer/ManageDealDrawerService';
import { fetchAllAvailableProducts } from '../../../store/investor/apiHelpers';
import { DEAL, DealStatus, MANDATE } from '../../../store/investor/reducer';
import { DEAL_VIEW_TYPES } from '../../../store/investor/constants';
import { useAppSelector } from '../../../app/hooks';
import { useSearchParams } from 'react-router-dom';
import { DataTableColumn } from './DataTable.types';

type Option = { value: string; label: string };

const USE_STATIC_FILTER_OPTIONS = ['customFields.partnerType'];

/**
 * Maps a filter source key to the dynamic filters it needs.
 * Add or remove flags here to customize API fetching per screen.
 */
const SOURCE_FILTER_CONFIG: Record<
    string,
    {
        fetchLenders?: boolean;
        fetchProducts?: boolean;
        fetchCities?: boolean;
    }
> = {
    MANDATE_VIEW: { fetchLenders: true, fetchProducts: true },
    DEAL_VIEW: { fetchLenders: true, fetchProducts: true },
    DEALS: { fetchLenders: true, fetchProducts: true }, // Added for deals page
    LEADS_PAGE: {},
    MANAGE_LENDERS: { fetchCities: true },
    MANAGE_PARTNERS: { fetchCities: true },
};

const useDynamicFilterOptions = (filterSourceKey: string = '', columns: DataTableColumn[]) => {
    const [lenders, setLenders] = useState<Option[]>([]);
    const [products, setProducts] = useState<Option[]>([]);
    const [cities, setCities] = useState<Option[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const userData = useAppSelector((state) => state.user.userData);
    const owners = useAppSelector((state) => state.investor.ownerDropdownOptions) || [];
    const dealStatusConfig = useAppSelector((state) => state.investor.deals.dealStatusConfig);
    const selectedCategory = searchParams.get('category') || DEAL_VIEW_TYPES.DEAL_VIEW;
    const isFilterDrawerOpen = useAppSelector((state) => state.investor.customFilterDrawer.open);
    const animationCompleteRef = useRef(false);
    const partnerData = useAppSelector((state) => state.investor.partnerData);

    /**
     * Fetch all dynamic filter options based on config for current source key.
     */
    const fetchAllOptions = useCallback(async () => {
        const config = SOURCE_FILTER_CONFIG[filterSourceKey] || {};

        try {
            setIsLoading(true);
            const promises: Promise<any>[] = [];

            if (config.fetchLenders) {
                promises.push(fetchDataByConfig({ parentOrgId: userData.investorOrgId }, 'GET_ALL_LENDERS', 'GET'));
            }

            if (config.fetchProducts) {
                promises.push(fetchAllAvailableProducts({ aggregatorId: userData.investorOrgId }, true));
            }

            // Handle cities API separately to avoid Promise.all issues
            let citiesRes: any = null;
            if (config.fetchCities) {
                try {
                    citiesRes = await getCitiesList({ parentOrgId: userData.investorOrgId });
                } catch (error) {
                    console.error('Cities API error:', error);
                    citiesRes = null;
                }
            }

            const results = await Promise.all(promises).catch((error) => {
                console.error('Promise.all error:', error);
                return [null, null];
            });
            const [lenderRes, productRes] = results;

            // Only update state if animation is complete
            if (!animationCompleteRef.current) {
                return;
            }

            if (config.fetchLenders && lenderRes) {
                const lenderOptions = lenderRes?.data?.lenderMap?.ADMIN
                    ? Object.entries(lenderRes.data.lenderMap.ADMIN).map(([value, label]: [string, any]) => ({
                          value,
                          label,
                      }))
                    : [];
                setLenders(lenderOptions);
            }

            if (config.fetchProducts && productRes) {
                const productOptions = productRes
                    ? Object.values(productRes).map((item: any) => ({
                          value: item?.productId || '',
                          label: item?.productName || '',
                      }))
                    : [];
                setProducts(productOptions);
            }

            if (config.fetchCities && citiesRes && citiesRes.data) {
                const citiesArr = citiesRes.data?.responseBody?.AICA_GET_CITIES_LIST?.cities;
                if (Array.isArray(citiesArr)) {
                    const cityOptions = citiesArr.map((city: string) => ({ value: city, label: city }));
                    setCities(cityOptions);
                } else {
                    setCities([]);
                }
            }
        } catch (err) {
            console.error('Error fetching dropdown options:', err);
            console.error('Error details:', err);
        } finally {
            setIsLoading(false);
        }
    }, [filterSourceKey, userData?.investorOrgId]);

    /**
     * Run fetch when drawer opens and userData is ready.
     * Delay the fetch to allow the background animation to complete smoothly.
     */
    useEffect(() => {
        if (isFilterDrawerOpen && userData?.investorOrgId) {
            // Reset animation flag
            animationCompleteRef.current = false;

            // Delay API calls by 600ms to allow the background animation to complete
            const timer = setTimeout(() => {
                animationCompleteRef.current = true;
                fetchAllOptions();
            }, 600);

            return () => clearTimeout(timer);
        } else if (!isFilterDrawerOpen) {
            // Reset the flag when drawer closes
            animationCompleteRef.current = false;
        }
        setIsLoading(false);
    }, [isFilterDrawerOpen, fetchAllOptions, userData?.investorOrgId]);

    /**
     * Owner dropdown options — derived from Redux.
     */
    const ownerOptions = useMemo<Option[]>(() => {
        return owners.map((owner) => ({
            value: owner?.userId || '',
            label: owner?.name || '',
        }));
    }, [owners]);

    /**
     * Status dropdown options — dynamic based on deal view type.
     */
    const dropdownStatusOptions = useMemo(() => {
        const isInvestorDealView = selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW;
        let statuses = selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW ? dealStatusConfig?.[DEAL] || [] : dealStatusConfig?.[MANDATE] || [];
        if (isInvestorDealView) {
            statuses = statuses.filter((option) => option.value !== DealStatus.INTERESTED);
        }
        return statuses;
    }, [selectedCategory]);

    /**
     * Final map of dynamic options for each field used in filter config.
     */
    const filterOptions: Record<string, Option[]> = useMemo(() => {
        const base: Record<string, Option[]> = {
            lenderName: lenders,
            loanTypes: products,
            dealStatus: dropdownStatusOptions,
        };

        // Assign owner options to any field that starts with "owners"
        columns.forEach((column) => {
            if (column?.field && column.field.startsWith('owners')) {
                base[column.field] = ownerOptions;
            }
        });

        // Handle organizationOwner field
        columns.forEach((column) => {
            if (column?.field && column.field === 'organizationOwner') {
                base[column.field] = ownerOptions;
            }
        });

        // Assign city options to any field that ends with "city" as a complete token
        columns.forEach((column) => {
            if (column?.field && column.field.toLowerCase().includes('.city')) {
                base[column.field] = cities;
            }
        });

        columns.forEach((column) => {
            if (
                column?.field &&
                USE_STATIC_FILTER_OPTIONS.includes(column?.field) &&
                column?.filterConfig?.options &&
                Array.isArray(column?.filterConfig?.options)
            ) {
                base[column.field] = column?.filterConfig?.options;
            }
        });

        if (partnerData) {
            base['customFields.partnerOrgId'] = partnerData
                .filter((org) => org?.organizationId && org?.organizationDisplayName)
                .map((org) => ({
                    value: org.organizationId || '',
                    label: org.organizationDisplayName || '',
                }));
        }

        return base;
    }, [lenders, products, dropdownStatusOptions, ownerOptions, cities, columns, partnerData]);

    return { filterOptions, isLoading };
};

export default useDynamicFilterOptions;
